import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import apiService from '../services/api';

const HomePage: React.FC = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate URL
    if (!url.trim()) {
      setError('Please enter a URL to scan');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.startScan(url);
      navigate(`/scan/${response.scanId}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start scan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 6,
            }}
          >
            <SecurityIcon
              sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}
            />
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              align="center"
              sx={{ mb: 1 }}
            >
              WebShield
            </Typography>
            <Typography
              variant="h5"
              component="p"
              align="center"
              color="text.secondary"
              sx={{ maxWidth: 800 }}
            >
              Scan websites for common security vulnerabilities and get detailed reports
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
              backgroundColor: 'white',
              mb: 6,
            }}
          >
            <Typography variant="h2" component="h2" gutterBottom align="center">
              Start a Scan
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                fullWidth
                required
                id="url"
                label="Website URL"
                name="url"
                placeholder="Enter the URL to scan (e.g., example.com)"
                variant="outlined"
                margin="normal"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                    Scanning...
                  </>
                ) : (
                  'Scan for Vulnerabilities'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h2" component="h2" gutterBottom align="center">
            What We Check
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                elevation={2}
                sx={{ height: '100%', borderTop: '4px solid #2e7d32' }}
              >
                <CardHeader title="Security Headers" />
                <CardContent>
                  <Typography variant="body1">
                    We check for essential security headers like Content-Security-Policy,
                    X-XSS-Protection, and Strict-Transport-Security that protect against
                    common web attacks.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                elevation={2}
                sx={{ height: '100%', borderTop: '4px solid #2e7d32' }}
              >
                <CardHeader title="XSS Vulnerabilities" />
                <CardContent>
                  <Typography variant="body1">
                    Our scanner detects potential Cross-Site Scripting (XSS) vulnerabilities
                    that could allow attackers to inject malicious scripts into your website.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                elevation={2}
                sx={{ height: '100%', borderTop: '4px solid #2e7d32' }}
              >
                <CardHeader title="Cookie Security" />
                <CardContent>
                  <Typography variant="body1">
                    We analyze cookie settings to ensure they have proper security flags
                    like Secure, HttpOnly, and SameSite to prevent session hijacking and CSRF attacks.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default HomePage; 